import { Alerts } from '@/components/assistant/alert/Alerts'
import { AvailableParams } from '@/routes/router'
import { Alert as AlertType } from '@/store/alert'
import { AlertStatus } from '@repo/shared/dtos/alert'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ScrollArea } from '@repo/ui/scrollArea'
import { cn } from '@repo/ui/utils'
import { BellRing } from 'lucide-react'
import { ReactNode } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'
import { useParams } from 'react-router-dom'

type AssistantProps = {
  alerts: AlertType[]
}

export function AssistantAlertsOnly(props: AssistantProps) {
  let alerts = props.alerts
  const { beneficiaryId } = useParams<AvailableParams>()

  const {
    data: [beneficiaries],
  } = useAsyncData([(state) => state.beneficiary.all])

  // Filter alerts by beneficiaryId if it's available
  alerts = beneficiaryId
    ? alerts.filter((alert) => alert.beneficiaryId === beneficiaryId)
    : alerts

  const hasUnreadAlerts =
    alerts.filter((alert) => alert.status === AlertStatus.UNREAD).length > 0

  if (alerts.length === 0) return
  return (
    <div className="flex h-full min-w-full items-end overflow-hidden px-0 py-4 lg:py-6 xl:py-7 2xl:px-8 2xl:py-20">
      <div className="relative flex h-full max-h-full min-w-full items-center justify-center overflow-hidden">
        <div className="relative flex h-full max-h-full min-w-full flex-col overflow-hidden rounded-xl">
          <Header
            alerts={alerts}
            rightSlot={
              alerts.length > 0 && (
                <div
                  className={cn(
                    'shadow-primary-600/50 z-10 mb-4 flex h-14 w-14 flex-shrink-0 select-none items-center justify-center rounded-full bg-opacity-85 text-lg text-white shadow transition-all',
                    hasUnreadAlerts ? 'bg-highlight' : 'bg-primary-900',
                  )}
                >
                  <div className={cn(hasUnreadAlerts && 'pt-2')}>
                    <BellRing
                      className={cn(hasUnreadAlerts && 'animate-bounce')}
                    />
                  </div>
                </div>
              )
            }
          />

          <ScrollArea className="h-full px-4">
            {alerts.length > 0 && beneficiaries && (
              <Alerts alerts={alerts} beneficiaries={beneficiaries} />
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}

function Header({
  alerts,
  rightSlot,
}: {
  alerts: AlertType[]
  rightSlot?: ReactNode
}) {
  const { t, isEN } = useT({ keyPrefix: 'assistant' })

  const unreadAlertsCount = alerts.filter(
    (alert) => alert.status === AlertStatus.UNREAD,
  ).length

  const alertMessageShown = unreadAlertsCount > 0

  return (
    <>
      <header className="mr-12 flex items-center justify-between gap-4">
        <div className="bg-primary-900 mb-4 ml-7 flex w-fit flex-shrink-0 flex-col gap-2 rounded-full bg-opacity-85 py-3.5 pl-6 pr-8">
          <div className="text-primary-100 flex select-none items-center gap-2">
            <IoSparklesSharp
              className={cn('h-6 w-6', alertMessageShown && 'text-emerald-500')}
            />

            <span
              className={cn(
                'flex flex-1 text-lg',
                alertMessageShown && 'text-emerald-500',
              )}
            >
              {alertMessageShown ? (isEN ? 'CaseAI:' : 'CaseAI :') : 'CaseAI'}
            </span>
            <span className="lowercase">
              {alertMessageShown &&
                t('alert.assistantMessage', { count: unreadAlertsCount })}
            </span>
          </div>
        </div>
        {rightSlot}
      </header>
    </>
  )
}
